import { type AppAbility, type Subject, defineAbility } from '@carii/casl-abilities';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api } from '../services/api.js';

export interface AuthUser {
  _id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  roles: Array<{ name: string }>;
  /** 334: the permissions the platform resolved for this person (`/auth/me`, since 207). Absent from an older
   * backend, in which case the shared definitions fall back to the built-in role defaults. */
  permissions?: ResolvedPermission[];
  avatar_url?: string | null;
}

/** One resolved permission, as `/auth/me` publishes it. */
export interface ResolvedPermission {
  subject: Subject;
  action: string;
  scope: 'any' | 'own_supplier' | 'own_assigned_cars';
}

export interface Membership {
  supplier_id: string;
  supplier_name: string | null;
  role: string;
}

interface AuthMeResponse {
  user: AuthUser & {
    role_ids: string[];
    active_supplier_id?: string | null;
    memberships?: Array<{ supplier_id: string; role: string }>;
  };
  roles: Array<{ name: string }>;
  // US3: /auth/me now surfaces the caller's supplier memberships and the active
  // one at the top level. Both optional for forward/back compatibility.
  memberships?: Membership[];
  active_supplier_id?: string | null;
  // 303 (contracts/auth-me-and-roles.md): always an array server-side, but optional here so an
  // older cached response (or a test fixture predating this field) still parses.
  assigned_car_ids?: string[];
  // 334 (#864): the resolved permissions the platform decides this person's requests from. Optional so an older
  // backend (or a fixture predating it) still parses.
  permissions?: ResolvedPermission[];
}

interface AuthState {
  user: AuthUser | null;
  ability: AppAbility;
  isAuthenticated: boolean;
  loading: boolean;
  memberships: Membership[];
  activeSupplierId: string | null;
  // 035 (R2/FR-003): the signed-in user is force-flagged to change their
  // password. The server gate blocks /auth/me for them, so they can't be fully
  // hydrated — the app must route them to the change-password screen instead.
  mustChangePassword: boolean;
}

/** The server's forced-password-change gate returns this code (035). */
const FORCE_CHANGE_CODE = 'PASSWORD_CHANGE_REQUIRED';

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  switchSupplier: (supplierId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function buildAbilityUser(me: AuthMeResponse) {
  return {
    _id: me.user._id,
    role_ids: me.user.role_ids,
    roles: me.roles,
    supplier_id: me.user.active_supplier_id ?? me.active_supplier_id ?? null,
    // 303 (research.md R2): carried through so a Host's `own_assigned_cars` condition resolves
    // against their REAL assigned cars client-side too, not just server-side — closing this
    // feature's own slice of the general "admin's AbilityUser never reads resolved `permissions`"
    // gap (plan.md Complexity Tracking), without attempting to close that gap in full here.
    assignedCarIds: me.assigned_car_ids ?? [],
    // 334 (#864, FR-001): the platform's own resolved set, so a custom role works in the panel exactly as on the
    // platform. Passed only when present: `undefined` keeps the shared definitions' built-in role defaults.
    ...(Array.isArray(me.permissions) ? { permissions: me.permissions } : {}),
  };
}

function deriveMemberships(me: AuthMeResponse): Membership[] {
  return me.memberships ?? [];
}

function deriveActiveSupplierId(me: AuthMeResponse): string | null {
  return me.active_supplier_id ?? me.user.active_supplier_id ?? null;
}

async function fetchMe(): Promise<AuthMeResponse> {
  return api.get<AuthMeResponse>('/auth/me');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({
    user: null,
    ability: defineAbility(null),
    isAuthenticated: false,
    loading: true,
    memberships: [],
    activeSupplierId: null,
    mustChangePassword: false,
  });

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    fetchMe()
      .then((me) => {
        const user: AuthUser = { ...me.user, roles: me.roles, permissions: me.permissions };
        setState({
          user,
          ability: defineAbility(buildAbilityUser(me)),
          isAuthenticated: true,
          loading: false,
          memberships: deriveMemberships(me),
          activeSupplierId: deriveActiveSupplierId(me),
          mustChangePassword: false,
        });
      })
      .catch((err) => {
        // A force-flagged user is authenticated but blocked from /auth/me — keep
        // the token and route them to the change-password screen (035 R2).
        if (err instanceof ApiError && err.code === FORCE_CHANGE_CODE) {
          setState({
            user: null,
            ability: defineAbility(null),
            isAuthenticated: false,
            loading: false,
            memberships: [],
            activeSupplierId: null,
            mustChangePassword: true,
          });
          return;
        }
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setState({
          user: null,
          ability: defineAbility(null),
          isAuthenticated: false,
          loading: false,
          memberships: [],
          activeSupplierId: null,
          mustChangePassword: false,
        });
      });
  }, []);

  async function login(email: string, password: string) {
    const { tokens } = await api.post<{
      user: unknown;
      tokens: { access_token: string; refresh_token: string };
    }>('/auth/login', { email, password });
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
    try {
      const me = await fetchMe();
      const user: AuthUser = { ...me.user, roles: me.roles, permissions: me.permissions };
      setState({
        user,
        ability: defineAbility(buildAbilityUser(me)),
        isAuthenticated: true,
        loading: false,
        memberships: deriveMemberships(me),
        activeSupplierId: deriveActiveSupplierId(me),
        mustChangePassword: false,
      });
    } catch (err) {
      // Force-flagged users can't load /auth/me — this is a successful login that
      // must land on the change-password screen, not an auth failure (035 R2).
      if (err instanceof ApiError && err.code === FORCE_CHANGE_CODE) {
        setState((s) => ({ ...s, mustChangePassword: true, loading: false }));
        return;
      }
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      throw err;
    }
  }

  async function logout() {
    const refresh_token = localStorage.getItem('refresh_token');
    if (refresh_token) {
      await api.post('/auth/logout', { refresh_token }).catch(() => {});
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setState({
      user: null,
      ability: defineAbility(null),
      isAuthenticated: false,
      loading: false,
      memberships: [],
      activeSupplierId: null,
      mustChangePassword: false,
    });
  }

  async function refreshUser() {
    const me = await fetchMe();
    const user: AuthUser = { ...me.user, roles: me.roles, permissions: me.permissions };
    setState((s) => ({
      ...s,
      user,
      ability: defineAbility(buildAbilityUser(me)),
      memberships: deriveMemberships(me),
      activeSupplierId: deriveActiveSupplierId(me),
      isAuthenticated: true,
      // Clearing the flag: once /auth/me succeeds, the gate is lifted (the user
      // completed the forced change), so drop the change-password routing.
      mustChangePassword: false,
    }));
  }

  async function switchSupplier(supplierId: string) {
    await api.post('/auth/switch-supplier', { supplier_id: supplierId });
    // Rebuild user/ability/memberships/active from the fresh /auth/me...
    await refreshUser();
    // ...then drop every cached query so all supplier-scoped data (cars, extras,
    // bookings, pricing, …) refetches under the newly active supplier.
    await queryClient.invalidateQueries();
  }

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refreshUser, switchSupplier }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
